import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PartnerSelector from './PartnerSelector';
import { trackError } from '../utils/error-tracking';
import { asError } from '../hooks/as-error';

interface AdminContextMenuProps {
  isVisible: boolean;
  position: { x: number; y: number };
  listingRect?: DOMRect;
  listingId: number;
  isFeatured?: boolean;
  onFeature: (listingId: number, shouldFeature: boolean) => Promise<void>;
  onFeatureForPartner: (listingId: number, membershipType: number, partnerName: string) => Promise<void>;
  onArchive: (listingId: number) => Promise<void>;
  /** true: out of every row and browse page, still live. false: back in (pages/api/listings/[id]/nsfw.ts). */
  onNsfw: (listingId: number, nsfw: boolean) => Promise<void>;
  /** Asked when the menu opens, so a listing already hidden offers "Not NSFW". */
  onLoadNsfw?: (listingId: number) => Promise<void>;
  /** Whether it is hidden now; null or absent when not known. */
  isNsfw?: boolean | null;
  onClose: () => void;
  isFeatureLoading?: boolean;
  isPartnerLoading?: boolean;
  isArchiveLoading?: boolean;
  isNsfwLoading?: boolean;
}

const AdminContextMenu: React.FC<AdminContextMenuProps> = ({
  isVisible,
  position,
  listingRect: _listingRect,
  listingId,
  isFeatured = false,
  onFeature,
  onFeatureForPartner,
  onArchive,
  onNsfw,
  onLoadNsfw,
  isNsfw = null,
  onClose,
  isFeatureLoading = false,
  isPartnerLoading = false,
  isArchiveLoading = false,
  isNsfwLoading = false
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [adjustedPosition, setAdjustedPosition] = useState<{ x: number; y: number; arrowOffset: number }>({
    x: position.x,
    y: position.y,
    arrowOffset: 50
  });

  // Position menu at click location with arrow pointing to it
  useEffect(() => {
    if (isVisible && menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const gap = 12; // Gap between cursor and menu

      // Position menu below the click point, centered on cursor
      let newX = position.x - (rect.width / 2);
      let newY = position.y + gap;

      // Calculate arrow offset (center by default)
      let arrowOffset = rect.width / 2;

      // Adjust if menu would go off right edge
      if (newX + rect.width > viewportWidth - 10) {
        const oldX = newX;
        newX = viewportWidth - rect.width - 10;
        arrowOffset = arrowOffset + (oldX - newX);
      }

      // Adjust if menu would go off left edge
      if (newX < 10) {
        const oldX = newX;
        newX = 10;
        arrowOffset = arrowOffset + (oldX - newX);
      }

      // If menu would go off bottom edge, position above the click
      if (newY + rect.height > viewportHeight - 10) {
        newY = position.y - rect.height - gap;
      }

      // Clamp arrow offset to stay within menu bounds
      arrowOffset = Math.max(16, Math.min(rect.width - 16, arrowOffset));

      setAdjustedPosition({ x: newX, y: newY, arrowOffset });
    }
  }, [isVisible, position]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handleScroll = (event: Event) => {
      // Don't close if scrolling within the menu itself (e.g., partner dropdown)
      const target = event.target;
      if (target instanceof Node && menuRef.current?.contains(target)) {
        return;
      }
      onClose();
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isVisible, onClose]);

  // Ask on each opening: Jev may have judged it since the grid loaded.
  useEffect(() => {
    if (isVisible) void onLoadNsfw?.(listingId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per opening, not per new callback identity
  }, [isVisible, listingId]);

  if (!isVisible) return null;

  const handleFeatureClick = async () => {
    try {
      await onFeature(listingId, true);
      onClose();
    } catch (error) {
      trackError(asError(error));
    }
  };

  const handlePartnerSelect = async (membershipType: number, partnerName: string) => {
    try {
      await onFeatureForPartner(listingId, membershipType, partnerName);
      onClose();
    } catch (error) {
      trackError(asError(error));
    }
  };

  const handleArchiveClick = async () => {
    try {
      await onArchive(listingId);
      onClose();
    } catch (error) {
      trackError(asError(error));
    }
  };

  const handleNsfwClick = async () => {
    try {
      await onNsfw(listingId, isNsfw !== true);
      onClose();
    } catch (error) {
      trackError(asError(error));
    }
  };

  const isAnyLoading = isFeatureLoading || isPartnerLoading || isArchiveLoading || isNsfwLoading;

  const menu = (
    <div
      ref={menuRef}
      className="fixed z-[9999]"
      // eslint-disable-next-line sellwild-code/no-inline-styles -- dynamic position from runtime calculation
      style={{
        top: `${adjustedPosition.y}px`,
        left: `${adjustedPosition.x}px`
      }}
    >
      {/* Arrow pointing to click location */}
      <div
        className="absolute w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-white dark:border-b-gray-800 -top-2"
        // eslint-disable-next-line sellwild-code/no-inline-styles -- dynamic arrow offset from runtime calculation
        style={{
          left: `${adjustedPosition.arrowOffset - 8}px`
        }}
      />

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-2">
        <div className="flex gap-2">
          {/* Feature/Refeature Button */}
          <button
            onClick={handleFeatureClick}
            disabled={isAnyLoading}
            className="px-3 py-2 border-2 rounded-lg transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed min-w-[85px] border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-white"
          >
            {isFeatureLoading ? '...' : (isFeatured ? 'Refeature' : 'Feature')}
          </button>

          {/* Partner Button */}
          <div className="relative">
            <PartnerSelector
              onSelect={handlePartnerSelect}
              buttonText={isPartnerLoading ? '...' : 'Partner'}
              className="[&>button]:px-3 [&>button]:py-2 [&>button]:border-2 [&>button]:border-blue-500 [&>button]:text-blue-500 [&>button]:bg-transparent [&>button]:rounded-lg [&>button]:hover:bg-blue-500 [&>button]:hover:text-white [&>button]:transition-colors [&>button]:font-semibold [&>button]:text-sm [&>button]:min-w-[70px]"
            />
          </div>

          {/* Archive Button */}
          <button
            onClick={handleArchiveClick}
            disabled={isAnyLoading}
            className="px-3 py-2 border-2 border-red-500 text-red-500 rounded-lg hover:bg-red-500 hover:text-white transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed min-w-[70px]"
          >
            {isArchiveLoading ? '...' : 'Archive'}
          </button>

          {/* NSFW: leaves rows and browse, stays live for typed search */}
          <button
            onClick={handleNsfwClick}
            disabled={isAnyLoading}
            data-testid="admin-menu-nsfw"
            className="px-3 py-2 border-2 border-rose-600 text-rose-600 rounded-lg hover:bg-rose-600 hover:text-white transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed min-w-[70px]"
          >
            {isNsfwLoading ? '...' : isNsfw === true ? 'Not NSFW' : 'NSFW'}
          </button>
        </div>
      </div>
    </div>
  );

  // Use portal to render at document root to avoid positioning issues. The
  // menu only opens on a click, so this never runs on the server.
  return createPortal(menu, document.body);
};

export default AdminContextMenu;
